import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { STATUS_LABELS } from "../../data/programData";
import { Activity } from "lucide-react";

const COLORS = {
  BELUM_MULAI: "#9ca3af",
  ON_PROGRESS: "#3b82f6",
  SELESAI: "#10b981",
  TERHAMBAT: "#ef4444",
};

const STATUS_BG = {
  BELUM_MULAI: "bg-gray-100 text-gray-600",
  ON_PROGRESS: "bg-blue-50 text-blue-600",
  SELESAI: "bg-emerald-50 text-emerald-600",
  TERHAMBAT: "bg-red-50 text-red-600",
};

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 text-[11.5px] shadow-md">
      <div className="font-semibold text-slate-900">{d.label}</div>
      <div className="text-slate-600">
        <b>{d.value}</b> sub-kegiatan ({d.percent}%)
      </div>
    </div>
  );
}

export default function StatusDonutChart({ subs }) {
  const total = subs.length;
  const counts = {
    BELUM_MULAI: 0,
    ON_PROGRESS: 0,
    SELESAI: 0,
    TERHAMBAT: 0,
  };
  for (const s of subs) counts[s.status] = (counts[s.status] || 0) + 1;

  const data = Object.keys(counts).map((k) => ({
    key: k,
    label: STATUS_LABELS[k],
    value: counts[k],
    percent: total > 0 ? Math.round((counts[k] / total) * 100) : 0,
  }));

  const hasData = total > 0;

  return (
    <div
      data-testid="chart-status-donut"
      className="rounded-2xl border border-sky-100/60 p-5 shadow-sm print:shadow-none h-full relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 55%)" }}
    >
      {/* Colored header band */}
      <div
        className="-mx-5 -mt-5 px-5 py-4 mb-4 rounded-t-2xl"
        style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <Activity className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-white leading-tight">
                Distribusi Status
              </h3>
              <p className="text-[11px] text-sky-100 mt-0.5">
                Komposisi sub-kegiatan
              </p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-white bg-white/20 px-2.5 py-1 rounded-full">
            {total} sub
          </span>
        </div>
      </div>

      {/* Donut chart */}
      <div style={{ width: "100%", height: 160 }}>
        {hasData ? (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data.filter((d) => d.value > 0)}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={3}
                stroke="#fff"
                strokeWidth={2}
              >
                {data
                  .filter((d) => d.value > 0)
                  .map((d) => (
                    <Cell key={d.key} fill={COLORS[d.key]} />
                  ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[11.5px] italic text-slate-400">
            Tidak ada data
          </div>
        )}
      </div>

      {/* Legend */}
      <ul className="mt-2 space-y-1.5">
        {data.map((d) => (
          <li
            key={d.key}
            data-testid={`donut-legend-${d.key}`}
            className="flex items-center justify-between gap-2 rounded-xl px-2.5 py-1.5 hover:bg-sky-50/50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: COLORS[d.key] }}
              />
              <span className="text-[12px] font-medium text-slate-700">{d.label}</span>
            </span>
            <span className="text-[12px] text-slate-600">
              <b>{d.value}</b>{" "}
              <span className="text-[10.5px] text-slate-400">({d.percent}%)</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
